/**
 * Which backoffice this is, from where it is served, and the marks that say so
 * outside production: a coloured dot on the favicon, "[INT]" / "[Local]" in
 * the tab title, and a badge in the top bar (EnvironmentBadge). Prod shows none.
 */
export type AppEnvironment = 'production' | 'integration' | 'local';

/** Where the production backoffice is served (its domain, and the Azure app behind it). */
const PRODUCTION_HOSTS = ['pro.ardealy.com', 'bo-prod-a6h2d5fngdfqehau.westeurope-01.azurewebsites.net'];

export function appEnvironment(hostname: string = window.location.hostname): AppEnvironment {
    const host = (hostname || '').toLowerCase();
    if (PRODUCTION_HOSTS.includes(host)) return 'production';
    if (
        host === 'localhost' ||
        host === '127.0.0.1' ||
        host === '[::1]' ||
        host.endsWith('.localhost') ||
        // The dev server opened from another machine on the LAN.
        /^(10|192\.168|172\.(1[6-9]|2\d|3[01]))\./.test(host)
    ) {
        return 'local';
    }
    return 'integration';
}

/** The dot on a non-production favicon: amber for INT, blue for a local server. */
export const ENVIRONMENT_DOT: Record<Exclude<AppEnvironment, 'production'>, string> = {
    integration: '#F59E0B',
    local: '#3B82F6',
};

const SIZE = 64;
/** The icon the dot is drawn on: the same artwork as favicon.ico, big enough to stay crisp. */
const BASE_ICON = `${process.env.PUBLIC_URL || ''}/logo192.png`;

/** The Ardealy icon with a dot in the bottom-right corner, as a PNG data URL. */
export function drawBadgedIcon(image: CanvasImageSource, color: string, doc: Document = document): string | null {
    const canvas = doc.createElement('canvas');
    canvas.width = SIZE;
    canvas.height = SIZE;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(image, 0, 0, SIZE, SIZE);
    const r = SIZE * 0.22;
    const cx = SIZE - r - 1;
    const cy = SIZE - r - 1;
    // A white ring first, so the dot stands out on the black icon and on a dark tab bar.
    ctx.beginPath();
    ctx.arc(cx, cy, r + 3, 0, Math.PI * 2);
    ctx.fillStyle = '#FFFFFF';
    ctx.fill();
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
    return canvas.toDataURL('image/png');
}

/** Point every favicon link of the page at this icon. */
export function setFavicon(href: string, doc: Document = document): void {
    const links = Array.from(doc.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]'));
    if (!links.length) {
        const link = doc.createElement('link');
        link.rel = 'icon';
        doc.head.appendChild(link);
        links.push(link);
    }
    links.forEach((link) => {
        link.type = 'image/png';
        link.href = href;
    });
}

/**
 * On INT and local servers, swap the favicon for the badged one. Production is
 * left alone. Never throws: at worst the tab keeps the plain icon.
 */
export function applyEnvironmentFavicon(
    env: AppEnvironment = appEnvironment(),
    doc: Document = document,
    loadImage: (src: string) => Promise<CanvasImageSource> = defaultLoadImage,
): Promise<void> {
    if (env === 'production') return Promise.resolve();
    return loadImage(BASE_ICON)
        .then((image) => {
            const href = drawBadgedIcon(image, ENVIRONMENT_DOT[env], doc);
            if (href) setFavicon(href, doc);
        })
        .catch(() => undefined);
}

function defaultLoadImage(src: string): Promise<CanvasImageSource> {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = reject;
        image.src = src;
    });
}

/** What the tab title starts with outside production. */
export const TITLE_PREFIX: Record<Exclude<AppEnvironment, 'production'>, string> = {
    integration: '[INT] ',
    local: '[Local] ',
};

/** A title with the environment's prefix, added once. */
export function prefixedTitle(title: string, env: AppEnvironment): string {
    if (env === 'production') return title;
    const prefix = TITLE_PREFIX[env];
    return title.startsWith(prefix) ? title : `${prefix}${title}`;
}

/**
 * Keep the environment's prefix on the tab title outside production, whatever
 * page sets it: the title element is watched and re-prefixed on each change.
 * Returns a function that stops watching.
 */
export function applyEnvironmentTitle(env: AppEnvironment = appEnvironment(), doc: Document = document): () => void {
    if (env === 'production') return () => undefined;
    const apply = () => {
        const next = prefixedTitle(doc.title, env);
        if (next !== doc.title) doc.title = next;
    };
    apply();
    let title = doc.querySelector('title');
    if (!title) {
        title = doc.createElement('title');
        doc.head.appendChild(title);
    }
    const observer = new MutationObserver(apply);
    observer.observe(title, { childList: true, characterData: true, subtree: true });
    return () => observer.disconnect();
}
